import * as React from 'react';
import './TaskAssignments.css';
import '../../common/EditableGridCell/EditableGridCell.css';
import { ActionButton, Checkbox, css, Icon } from 'office-ui-fabric-react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { ITask, ITaskAttrs } from '../../../entities/Subentities';
import { Dictionary, IUserInfo } from '../../../entities/common';
import { nameof } from '../../../store/services/metadataService';
import { ASSIGNMENT_UNITS_NEGATIVE_ERROR } from '../Fields';
import * as ResourcesListStore from '../../../store/ResourcesListStore';
import { ResourceLoadingTypes } from '../../../store/ResourcesListStore';
import PersonPickerInput, { PersonInfo } from '../../common/inputs/PersonPickerInput';
import NumberInput from '../../common/inputs/NumberInput';
import SelectionModeSwitchableCommandBar from '../../common/SelectionModeSwitchableCommandBar';

const DEFAULT_UNITS = 100;

const idsStillAssigned = (ids: string[], assignees: IUserInfo[]): string[] =>
    ids.filter(id => assignees.some(a => a.id === id));

// Inline click-to-edit Units (%) cell, styled like the Configure Effective Max Units grid cells
// (shared .grid-editable-cell look: formatted value with a hover outline; a green-outlined input on click).
const UnitsCell = (props: { value?: number; readonly: boolean; error?: string; onChange: (value: number | null) => void }) => {
    const [editing, setEditing] = React.useState(false);

    if (props.readonly) {
        return <div className="grid-editable-cell readonly">
            <div className="formatter"><span className="text-formatter">{props.value == null ? '100%' : `${props.value}%`}</span></div>
        </div>;
    }

    // A null/absent Units is the 100% default (old tasks predate the field) — show and edit it as 100%.
    const displayValue = props.value ?? DEFAULT_UNITS;
    const cell = editing
        ? <div className="grid-editable-cell in-edit-mode assignment-units-editor">
            <NumberInput
                value={displayValue}
                selectOnFocus
                inputRef={input => input?.focus()}
                inputProps={{ suffix: '%' }}
                onEditComplete={value => { setEditing(false); props.onChange(value); }} />
        </div>
        : <button
            type="button"
            className={css("grid-editable-cell", "grid-editable-cell-button", props.error && "is-invalid")}
            onClick={() => setEditing(true)}
            onKeyDown={e => { if (e.key === 'F2') { setEditing(true); } }}>
            <div className="formatter"><span className="text-formatter">{`${displayValue}%`}</span></div>
        </button>;

    // Validation shows as an inline pill styled like the tasks-list grid error callout. It is rendered in-flow
    // (not a portal Callout) so it isn't hidden behind the high z-index edit panel.
    return <div className="assignment-units-cell">
        {cell}
        {props.error &&
            <div className="assignment-units-error">
                <Icon iconName="ErrorBadge" />
                <span className="info-message">{props.error}</span>
            </div>}
    </div>;
};

interface OwnProps {
    readonly: boolean;
    task: ITask;
    onChange: (fieldName?: string, fieldValue?: unknown, extra?: Dictionary<unknown>) => void;
}

type ActionProps = {
    resourcesActions: typeof ResourcesListStore.actionCreators;
};

type Props = OwnProps & ActionProps;

// US 15956 – Assignments tab. Lists task assignees with an inline-editable Units (%) column and adds/removes
// assignees (rows are selected via checkboxes and removed from a toolbar Delete action — ID 5/5.1). It only EMITS
// the assignment change (membership or per-assignee Units); the resulting dates/Effort/Units are derived by the
// server on save and previewed by the panel's central derivation, so this tab and the Assigned To field on the
// Details tab always show the same values (ID 6.2 / ID 3) without duplicating any schedule math here.
const TaskAssignments = (props: Props) => {
    const { readonly, task, onChange, resourcesActions } = props;

    const assignees = React.useMemo(() => task.attributes.AssignedTo ?? [], [task.attributes.AssignedTo]);
    // US 15956: Units is editable on both task types (Fixed Units → drives Duration; Flexible → drives Duration
    // too, and is re-derived when Effort/Duration/dates change server-side).
    const unitsEditable = !readonly;
    // The "Select a resource…" picker appears only after + Add Assignee is clicked (ID 2.1); it stays open and
    // resets after each add for seamless addition of several assignees (ID 2.2).
    const [adding, setAdding] = React.useState(false);
    const [pickerKey, setPickerKey] = React.useState(0);
    const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

    // Collapse the picker back to the + Add Assignee button when it loses focus (the picker keeps focus after
    // selecting a suggestion, so this only fires on a real click-away).
    const stopAdding = React.useCallback(() => setAdding(false), []);

    // Drop selections for assignees that no longer exist (e.g. after a delete).
    React.useEffect(() => {
        setSelectedIds(prev => idsStillAssigned(prev, assignees));
    }, [assignees]);

    // A MEMBERSHIP add/remove: send only the new assignee list. Dates/Effort/Units are derived by the server on save
    // and previewed by the panel's central derivation (Fields.buildDeriveDisplayAttributes) — the client must never
    // send client-derived dates (see the duration.ts / TaskWorkloadHandler headers). A membership change supersedes
    // nothing (any pending Duration/date edit is the base the engine extends), so no `extra`.
    const applyAssignees = React.useCallback((newAssignees: IUserInfo[]) => {
        onChange(nameof<ITaskAttrs>("AssignedTo"), newAssignees);
    }, [onChange]);

    // A per-assignment UNITS edit (same membership). Send the authoritative per-assignee Units and supersede any
    // pending Duration/Due — the server's TryApplyUnitsEditRules (both task types) keeps each assignee's edited Units
    // and sets Duration to the LONGEST per-assignee span (a 50% assignee stretches the task while a 100% one finishes
    // early). The panel's central derivation (Fields.buildDeriveDisplayAttributes) previews the same. This runs only
    // for an unchanged assignee set — and for a FIRST assignment, where a stated Units is honored the same way (the
    // engine reads "no Units" as "derive it", which is why a newly picked assignee carries none). A pending MEMBERSHIP
    // change is what the engine cannot resolve from the Units alone, since its rule keys off the persisted assignee
    // set: a REMOVAL on either task type, and an ADD on a Fixed Units one (a Flexible add it now reads itself —
    // TryApplyUnitsEditRules' addWithStatedUnits). There the panel replays the result and sends the one value the
    // engine cannot recover (Fields.buildTaskSavePayload): the span for a removal, and for a Fixed Units add the
    // EFFORT the add settled — a span there is re-read as a per-assignee own-day count and re-extends the task.
    // Superseding DELETES the Duration/Due edit, so it needs something to fall back on. On the CREATE panel there is no
    // stored span, and a Duration the user typed before touching Units would be the only span there is — dropping it
    // falls back to the fabricated 1-day new-task default (a typed 3d collapsed to 1d the moment Units were edited).
    // Keep it there: the units edit still wins in the preview (the derivation re-derives the span from Effort), and the
    // create payload carries that derived span, so nothing stale is sent. Mirrors the Effort field's rule in Fields.tsx.
    const onUnitsChange = React.useCallback((assigneeId: string, units: number | null) => {
        const newAssignees = assignees.map(a => a.id === assigneeId ? { ...a, units: units ?? undefined } : { ...a });
        const supersede = task.id
            ? { [nameof<ITaskAttrs>("Duration")]: undefined, [nameof<ITaskAttrs>("DueDate")]: undefined }
            : {};
        onChange(nameof<ITaskAttrs>("AssignedTo"), newAssignees, supersede);
    }, [assignees, onChange, task.id]);

    const deleteSelected = React.useCallback(() => {
        const selected = new Set(selectedIds);
        setSelectedIds([]);
        applyAssignees(assignees.filter(a => !selected.has(a.id)).map(a => ({ ...a })));
    }, [assignees, selectedIds, applyAssignees]);

    const toggleOne = React.useCallback((id: string, checked?: boolean) => {
        setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
    }, []);

    const toggleAll = React.useCallback((checked?: boolean) => {
        setSelectedIds(checked ? assignees.map(a => a.id) : []);
    }, [assignees]);

    // On selection: blank the picker immediately (no lingering chip), load the resource's calendar so the
    // Fixed Units preview is accurate, then add the row. Mirrors the seamless inline Add Task flow.
    const onPick = React.useCallback((value: PersonInfo | PersonInfo[] | null) => {
        setPickerKey(k => k + 1);
        const person = Array.isArray(value) ? value[0] : value;
        if (!person || assignees.some(a => a.id === person.id)) {
            return;
        }
        // US 15956 – a JUST-PICKED assignee carries NO Units, the same as a pick in the Assigned To field (Fields.tsx
        // withAssignmentUnits): an absent Units means "derive it" (UC3/UC4) and an explicit one means "the user said
        // so". Stamping the default made the two indistinguishable, so the engine read a plain add as a typed Units —
        // which after Bug 17159 (TryApplyUnitsEditRules' addWithStatedUnits) decides the span: adding a resource to a
        // 2d/16h Flexible task would have saved 3d at 100% where both the tab and the panel previewed 4d at 50%. The
        // row still SHOWS 100% while the Units are unset (UnitsCell's DEFAULT_UNITS fallback, as the engine's
        // GetUnitsFactor does), and the panel's derivation fills in the real number as soon as it runs.
        const newAssignee: IUserInfo = { id: person.id, fullName: person.fullName, imageId: person.imageId ?? '' };
        resourcesActions.loadResourcesByIds([person.id], ResourceLoadingTypes.Calendar, undefined, true,
            () => applyAssignees([...assignees.map(a => ({ ...a })), newAssignee]));
    }, [assignees, applyAssignees, resourcesActions]);

    const allSelected = assignees.length > 0 && selectedIds.length === assignees.length;
    const someSelected = selectedIds.length > 0 && !allSelected;

    return <div className="task-assignments-tab">
        {!readonly && selectedIds.length > 0 &&
            <SelectionModeSwitchableCommandBar
                items={[]}
                selectionMode={{
                    enabled: true,
                    selectedCount: selectedIds.length,
                    items: [{ key: 'delete', text: 'Delete', iconProps: { iconName: 'Delete' }, onClick: deleteSelected }],
                    onCancel: () => setSelectedIds([])
                }} />
        }

        {assignees.length > 0 &&
            <div className="task-assignments-grid" role="grid">
                <div className="task-assignments-row task-assignments-header" role="row">
                    <div className="col-check" role="columnheader">
                        {!readonly &&
                            <Checkbox
                                checked={allSelected}
                                indeterminate={someSelected}
                                onChange={(_, checked) => toggleAll(checked)}
                                ariaLabel="Select all assignees" />}
                    </div>
                    <div className="col-resource" role="columnheader">Resource Name</div>
                    <div className="col-units" role="columnheader">Units</div>
                </div>
                {assignees.map(a => {
                    const unitsError = a.units != null && a.units < 0
                        ? ASSIGNMENT_UNITS_NEGATIVE_ERROR
                        : undefined;
                    const selected = selectedIds.includes(a.id);
                    return <div className={css("task-assignments-row", selected && "is-selected")} role="row" key={a.id}>
                        <div className="col-check" role="gridcell">
                            {!readonly &&
                                <Checkbox
                                    checked={selected}
                                    onChange={(_, checked) => toggleOne(a.id, checked)}
                                    ariaLabel={`Select ${a.fullName}`} />}
                        </div>
                        <div className="col-resource" role="gridcell">{a.fullName}</div>
                        <div className="col-units" role="gridcell">
                            <UnitsCell
                                value={a.units ?? undefined}
                                readonly={!unitsEditable}
                                error={unitsEditable ? unitsError : undefined}
                                onChange={units => onUnitsChange(a.id, units)} />
                        </div>
                    </div>;
                })}
            </div>
        }

        {!readonly && (adding
            ? <div className="task-assignments-add">
                <PersonPickerInput
                    key={pickerKey}
                    searchUrl="api/resource/find"
                    filter={{ exceptIds: assignees.map(a => a.id) }}
                    inputProps={{ placeholder: 'Select a resource...', autoFocus: true }}
                    onBlur={stopAdding}
                    onChanged={onPick} />
            </div>
            : <ActionButton
                className="task-assignments-add-btn"
                iconProps={{ iconName: 'Add' }}
                onClick={() => setAdding(true)}>Add Assignee</ActionButton>)
        }
    </div>;
};

function mergeActionCreators(dispatch: any): ActionProps {
    return { resourcesActions: bindActionCreators(ResourcesListStore.actionCreators, dispatch) };
}

export default connect(undefined, mergeActionCreators)(TaskAssignments);
